/**
 * Share-link encoding. A share link is a Preset's canonical routePath (its identity)
 * with the viewer's lens layered on as query view-params - never folded into the
 * path. The lens is Keyboard + Keyboard Layout; compare adds the B-side Profile and
 * its own Keyboard + Layout within the both-Presets shareability boundary. These functions
 * are pure and window-free: the caller prepends the origin to share, and the router's
 * `validateSearch` calls parseLensView to decode.
 */

/** The lens (and compare state) a share link carries as query params on a Preset path. */
export interface LensView {
  /** A-side Keyboard id; built-in only, omitted for a session import (not URL-addressable). */
  kbd?: string;
  /** A-side Keyboard Layout id (universal or a built-in board's layer). */
  layout?: string;
  /** Compare mode on. The B-side fields below are meaningful only when this is set. */
  cmp?: boolean;
  /** B-side Profile: a catalog Preset id (never "uploaded" - an upload is non-shareable). */
  pb?: string;
  /** B-side Keyboard id; omitted means "Same as A". */
  kbdB?: string;
  /** B-side Keyboard Layout id; omitted means "Same as A" / the B board's default. */
  layoutB?: string;
}

// the query-param spelling. Short but readable; the ids themselves are already URL-safe
// (lowercase, hyphenated) so no escaping is ever needed.
const PARAM = {
  kbd: "kbd",
  layout: "layout",
  cmp: "cmp",
  pb: "pb",
  kbdB: "kbdB",
  layoutB: "layoutB",
} as const;

/**
 * The shareable root-relative URL for a Preset: its routePath plus the lens as query params,
 * emitted in a fixed order and only when set, so an empty lens yields the bare path. The
 * compare params (pb/kbdB/layoutB) are emitted only under `cmp`, so a stale B selection can
 * never leak into a single-board link.
 */
export function buildShareUrl(routePath: string, lens: LensView): string {
  const params = new URLSearchParams();
  if (lens.kbd) params.set(PARAM.kbd, lens.kbd);
  if (lens.layout) params.set(PARAM.layout, lens.layout);
  if (lens.cmp) {
    params.set(PARAM.cmp, "1");
    if (lens.pb) params.set(PARAM.pb, lens.pb);
    if (lens.kbdB) params.set(PARAM.kbdB, lens.kbdB);
    if (lens.layoutB) params.set(PARAM.layoutB, lens.layoutB);
  }
  const query = params.toString();
  return query ? `${routePath}?${query}` : routePath;
}

const asStr = (value: unknown): string | undefined =>
  typeof value === "string" && value !== "" ? value : undefined;

/**
 * Decode a router search record into a LensView (the TanStack `validateSearch` shape). Unknown,
 * missing, non-string, or empty fields drop out; `cmp` is truthy only for "1" or boolean true.
 * The compare-only fields are read only when `cmp` is set, so a stray B param can never
 * half-open compare mode on a freshly opened single-board link.
 */
export function parseLensView(search: Record<string, unknown>): LensView {
  const view: LensView = {};
  const kbd = asStr(search[PARAM.kbd]);
  if (kbd) view.kbd = kbd;
  const layout = asStr(search[PARAM.layout]);
  if (layout) view.layout = layout;
  if (search[PARAM.cmp] === "1" || search[PARAM.cmp] === true) {
    view.cmp = true;
    const pb = asStr(search[PARAM.pb]);
    if (pb) view.pb = pb;
    const kbdB = asStr(search[PARAM.kbdB]);
    if (kbdB) view.kbdB = kbdB;
    const layoutB = asStr(search[PARAM.layoutB]);
    if (layoutB) view.layoutB = layoutB;
  }
  return view;
}
